import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Sparkles, Award, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Full-screen celebration modal fired when the tourist completes a mission.
 * Shows confetti + mission info + earned badges popping up in sequence.
 *
 * Props:
 *  - completions: array of { id, name, reward_points, awarded_badges: [{name, image, description}] }
 *  - onClose: () => void
 */
export default function MissionCompletionModal({ completions, onClose }) {
  const item = completions?.[0];
  const firedFor = useRef(null);

  useEffect(() => {
    if (!item || firedFor.current === item.id) return;
    firedFor.current = item.id;
    // Confetti burst
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#8404ab', '#f3b201', '#ff6ec4', '#22c55e', '#3b82f6'];
    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 65,
        origin: { x: 0, y: 0.75 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 65,
        origin: { x: 1, y: 0.75 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    // Big center burst
    confetti({ particleCount: 120, spread: 90, origin: { y: 0.4 }, colors, scalar: 1.2 });
  }, [item]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          data-testid="mission-completion-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.7, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 14, stiffness: 200 }}
            className="relative w-full max-w-sm bg-gradient-to-br from-primary via-primary/95 to-secondary text-white rounded-3xl p-6 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="mission-completion-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Sparkles */}
            <Sparkles className="absolute top-6 left-6 w-5 h-5 opacity-40 animate-pulse" />
            <Sparkles className="absolute bottom-24 right-8 w-4 h-4 opacity-60 animate-pulse" style={{ animationDelay: '0.4s' }} />

            <div className="text-center relative z-10">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 8, delay: 0.15 }}
                className="w-20 h-20 mx-auto rounded-full bg-yellow-400 flex items-center justify-center shadow-xl mb-3"
              >
                <Trophy className="w-10 h-10 text-yellow-900" />
              </motion.div>
              <p className="text-xs font-bold uppercase tracking-widest opacity-90">Misión completada</p>
              <h2 className="text-2xl font-black mt-1">{item.name}</h2>
              {item.description && <p className="text-sm opacity-90 mt-1">{item.description}</p>}

              {item.reward_points > 0 && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6, type: 'spring' }}
                  className="inline-flex items-center gap-1.5 mt-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-black shadow"
                >
                  <Sparkles className="w-4 h-4" /> +{item.reward_points} puntos
                </motion.div>
              )}

              {item.awarded_badges?.length > 0 && (
                <div className="mt-5">
                  <p className="text-[10px] uppercase tracking-widest opacity-80 mb-2">Insignias ganadas</p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    {item.awarded_badges.map((b, i) => (
                      <motion.div
                        key={b.id || b.name}
                        initial={{ scale: 0, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ delay: 0.9 + i * 0.15, type: 'spring', damping: 10 }}
                        className="flex flex-col items-center gap-1"
                      >
                        <div className="w-16 h-16 rounded-full bg-yellow-100 border-4 border-yellow-300 shadow-lg flex items-center justify-center overflow-hidden">
                          {b.image
                            ? <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                            : <Award className="w-8 h-8 text-yellow-700" />}
                        </div>
                        <p className="text-[10px] font-bold max-w-[70px] line-clamp-2 text-center">{b.name}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {completions.length > 1 && (
                <p className="text-[11px] opacity-80 mt-4">+{completions.length - 1} misión{completions.length - 1 > 1 ? 'es' : ''} más completada{completions.length - 1 > 1 ? 's' : ''}</p>
              )}

              <Button
                onClick={onClose}
                data-testid="mission-completion-continue"
                className="w-full mt-5 rounded-full bg-white text-primary hover:bg-white/90 font-bold shadow-lg"
              >
                ¡Genial, seguir explorando!
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
